import Tooltip from "@mui/material/Tooltip";
import React, {
  Dispatch,
  RefCallback,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { AiOutlineInfoCircle } from "react-icons/ai";
import { toast } from "react-toastify";
import {
  InteractionsTaken,
  RecordingSessionType,
  SessionItemType,
} from "../__generated__/graphql";
import { useCompany } from "../contexts/CompanyContext";
import {
  DEMO_COMPANY_NAME,
  INACTIVE_PERIOD_THRESHOLD_MS,
  WEB_PLAYER_TOAST_CONTAINER_ID,
} from "../utils/constants";
import { TimelineEvent } from "../utils/interfaces";
import { ContextMenu, MenuItem } from "./ContextMenu";

const DRAG_HANDLE = {
  LEFT: "left",
  RIGHT: "right",
  REGION: "region",
} as const;

type DragHandleType = (typeof DRAG_HANDLE)[keyof typeof DRAG_HANDLE];

interface RecordingsTimelineProps {
  contentRef: RefCallback<HTMLDivElement | null>;
  currentPosition?: number;
  currentSection?: [number, number] | null;
  displayOnly?: boolean;
  deviceUserInteractions?: InteractionsTaken;
  hoverTime?: number | null;
  isDraggable?: boolean;
  isMoving?: boolean;
  isSelectable?: boolean;
  offset: number;
  recordings: RecordingSessionType[] | SessionItemType[];
  scale: number;
  selectedRegion?: Region | null;
  showEvents?: boolean;
  timeline: [number, number];
  timelineEvents?: TimelineEvent[];
  visibleStart: number;
  visibleEnd: number;
  zoomDragStart: number | null;
  zoomDragEnd: number | null;
  onTimelineClick?: (recording: RecordingSessionType, time: number) => void;
  setSelectedRegion?: Dispatch<SetStateAction<Region | null>>;
  resetZoom: () => void;
}

export interface Region {
  start: number;
  end: number;
}

const RecordingsTimeline: React.FC<RecordingsTimelineProps> = ({
  contentRef,
  currentPosition,
  currentSection,
  displayOnly,
  hoverTime,
  isDraggable,
  isMoving,
  isSelectable,
  offset,
  recordings,
  deviceUserInteractions,
  scale,
  selectedRegion,
  showEvents,
  timeline,
  timelineEvents,
  visibleStart,
  visibleEnd,
  zoomDragStart,
  zoomDragEnd,
  onTimelineClick,
  setSelectedRegion,
  resetZoom,
}) => {
  const recordingsRef = useRef<(HTMLDivElement | null)[]>([]);
  const innerContentRef = useRef<HTMLDivElement | null>(null);

  const startXRef = useRef(0);
  const initialRegionRef = useRef<Region | null>(null);
  const [dragHandle, setDragHandle] = useState<null | DragHandleType>(null);
  const { selectedCompany } = useCompany();
  const [contextMenu, setContextMenu] = useState<{
    x: number;
    y: number;
  } | null>(null);

  const totalMs = useMemo(() => timeline[1] - timeline[0], [timeline]);
  const isDemoCompany = selectedCompany?.label === DEMO_COMPANY_NAME;

  const handleContextMenu = (event: React.MouseEvent) => {
    event.preventDefault();
    setContextMenu({ x: event.clientX, y: event.clientY });
  };

  const menuItems: MenuItem[] = [
    {
      title: "Zoom to 2-weeks",
      onClick: resetZoom,
    },
  ];

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (
        !dragHandle ||
        !selectedRegion ||
        !setSelectedRegion ||
        !innerContentRef.current
      )
        return;

      const rect = innerContentRef.current.getBoundingClientRect();
      const containerWidth = rect.width;
      const visibleMs = totalMs / scale;
      const msPerPixel = visibleMs / containerWidth;
      const deltaPx = e.clientX - startXRef.current;
      const deltaMs = deltaPx * msPerPixel;

      const handlePx = 2;
      const minDuration = 1000;
      const safeMinMs = Math.max(msPerPixel * handlePx, minDuration);

      const min = timeline[0];
      const max = timeline[1];

      if (dragHandle === DRAG_HANDLE.LEFT && initialRegionRef.current) {
        let newStart = initialRegionRef.current.start + deltaMs;
        newStart = Math.max(
          min,
          Math.min(newStart, initialRegionRef.current.end - safeMinMs),
        );
        setSelectedRegion({
          start: newStart,
          end: initialRegionRef.current.end,
        });
      }

      if (dragHandle === DRAG_HANDLE.RIGHT && initialRegionRef.current) {
        let newEnd = initialRegionRef.current.end + deltaMs;
        newEnd = Math.min(
          max,
          Math.max(newEnd, initialRegionRef.current.start + safeMinMs),
        );
        setSelectedRegion({
          start: initialRegionRef.current.start,
          end: newEnd,
        });
      }

      if (dragHandle === DRAG_HANDLE.REGION && initialRegionRef.current) {
        const duration =
          initialRegionRef.current.end - initialRegionRef.current.start;
        let newStart = initialRegionRef.current.start + deltaMs;
        let newEnd = newStart + duration;

        if (newStart < min) {
          newStart = min;
          newEnd = newStart + duration;
        }
        if (newEnd > max) {
          newEnd = max;
          newStart = newEnd - duration;
        }

        setSelectedRegion({ start: newStart, end: newEnd });
      }
    };

    const handleMouseUp = () => setDragHandle(null);

    if (dragHandle) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [dragHandle, selectedRegion, setSelectedRegion, scale, timeline, totalMs]);

  const visibleMs = totalMs / scale;
  const activityPeriodMinWidthPx = visibleMs > 7 * 24 * 3600 * 1000 ? 8 : 16;

  const interactionRanges = useMemo(() => {
    let allTimestamps = [];
    if (deviceUserInteractions) {
      allTimestamps = [
        ...(deviceUserInteractions.clicks || []).map(({ timestamp }) =>
          parseInt(timestamp || "", 10),
        ),
        ...(deviceUserInteractions.pageNavigations || []).map(({ timestamp }) =>
          parseInt(timestamp || "", 10),
        ),
        ...(deviceUserInteractions.textEdits || []).map(({ timestamp }) =>
          parseInt(timestamp || "", 10),
        ),
      ].filter(Boolean);
    } else {
      allTimestamps = recordings
        .map((recording) => {
          const { userInteractions } = recording;
          if (userInteractions && "clicks" in userInteractions) {
            return [
              ...(userInteractions.clicks || []).map(({ timestamp }) =>
                parseInt(timestamp || "", 10),
              ),
              ...(userInteractions.pageNavigations || []).map(({ timestamp }) =>
                parseInt(timestamp || "", 10),
              ),
              ...(userInteractions.textEdits || []).map(({ timestamp }) =>
                parseInt(timestamp || "", 10),
              ),
            ].filter(Boolean);
          }
          return ((userInteractions as any[]) || []).filter(Boolean);
        })
        .flat();
    }

    allTimestamps = allTimestamps.sort((a, b) => a - b);

    const interactionRanges: { start: number; end: number; count: number }[] =
      [];
    if (!allTimestamps.length) return interactionRanges;

    let divider = visibleMs / 200;
    if (innerContentRef.current) {
      const rect = innerContentRef.current?.getBoundingClientRect();
      const containerWidth = rect?.width;
      const msPerPixel = visibleMs / containerWidth;
      divider = activityPeriodMinWidthPx * msPerPixel;
    }

    let start = allTimestamps[0];
    let end = allTimestamps[0];
    let count = 1;

    for (let i = 1; i < allTimestamps.length; i++) {
      if (
        allTimestamps[i] - end <= INACTIVE_PERIOD_THRESHOLD_MS ||
        allTimestamps[i] - start < divider
      ) {
        end = allTimestamps[i];
        count++;
      } else {
        interactionRanges.push({
          start,
          end: end + INACTIVE_PERIOD_THRESHOLD_MS,
          count,
        });
        start = allTimestamps[i];
        end = allTimestamps[i];
        count = 1;
      }
    }

    // Push the last group
    interactionRanges.push({
      start,
      end: end + INACTIVE_PERIOD_THRESHOLD_MS,
      count,
    });

    return interactionRanges;
  }, [activityPeriodMinWidthPx, recordings, visibleMs]);

  const fetchNextRecording = useCallback(
    (timestamp: number) => {
      if (!onTimelineClick) return;
      const nextRecording = (recordings as RecordingSessionType[]).find(
        ({ startTimestamp }) => startTimestamp > timestamp,
      );

      if (nextRecording) {
        if (!isDemoCompany) {
          toast.info(
            <div className='text-sm font-medium'>
              No activity was in the given section
            </div>,
            {
              containerId: WEB_PLAYER_TOAST_CONTAINER_ID,
            },
          );
        }
        onTimelineClick(nextRecording, nextRecording.startTimestamp);
        return;
      }
      toast.info(
        <div className='text-sm font-medium'>
          There is no more activity for this user
        </div>,
        {
          containerId: WEB_PLAYER_TOAST_CONTAINER_ID,
        },
      );
      const lastRecording = (recordings as RecordingSessionType[])[
        recordings.length - 1
      ];
      onTimelineClick(
        lastRecording,
        lastRecording.startTimestamp + lastRecording.durationMs,
      );
    },
    [isDemoCompany, onTimelineClick, recordings],
  );

  const events = useMemo(() => {
    if (visibleMs > 60 * 60 * 1000 || !timelineEvents) return null;

    // Use buffer for smoother UX
    const bufferMs = 30_000;
    const startLimit = visibleStart - bufferMs;
    const endLimit = visibleEnd + bufferMs;

    return timelineEvents
      .filter(({ time }) => time >= startLimit && time <= endLimit)
      .map(({ time, text, recordingId }, i) => {
        const startOffset =
          time < timeline[0] ? 0 : ((time - timeline[0]) / totalMs) * 100;
        return (
          <Tooltip key={i} title={text} placement='top'>
            <svg
              stroke='currentColor'
              fill='none'
              strokeWidth='0'
              viewBox='0 0 24 24'
              className='absolute z-30 top-3.5 -translate-x-1/2 cursor-pointer'
              data-testid='userActionPointer'
              height='18'
              width='18'
              xmlns='http://www.w3.org/2000/svg'
              style={{
                left: `${startOffset * scale}%`,
              }}
              {...(onTimelineClick && {
                onClick: (e) => {
                  if (isMoving || e.shiftKey || !displayOnly) return;

                  const recording = (recordings as RecordingSessionType[]).find(
                    ({ id }) => id === recordingId,
                  );

                  if (!recording) {
                    fetchNextRecording(time);
                    return;
                  }
                  onTimelineClick(recording, time);
                },
              })}
            >
              <circle cx='12' cy='12' r='6' fill='black' />
              <circle cx='12' cy='12' r='4' fill='white' />
            </svg>
          </Tooltip>
        );
      });
  }, [
    displayOnly,
    fetchNextRecording,
    isMoving,
    onTimelineClick,
    recordings,
    scale,
    timeline,
    timelineEvents,
    totalMs,
    visibleEnd,
    visibleMs,
    visibleStart,
  ]);

  return (
    <div
      className={`relative flex-1 overflow-hidden ${
        showEvents ? "pt-10 mt-[-40px]" : ""
      }`}
    >
      <div
        ref={(el) => {
          innerContentRef.current = el;
          contentRef(el); // forward to parent map
        }}
        className={`relative bg-zinc-200 h-full ${
          isDraggable ? "cursor-grab" : isSelectable ? "cursor-crosshair" : ""
        }`}
        {...(isSelectable && { onContextMenu: handleContextMenu })}
        {...(onTimelineClick && {
          onClick: (e) => {
            if (isMoving || e.shiftKey || !displayOnly) return;
            const rect = innerContentRef.current?.getBoundingClientRect();

            if (!rect) return;
            const timestamp =
              visibleStart +
              ((e.clientX - rect.left) / rect.width) *
                (visibleEnd - visibleStart);

            const recording = (recordings as RecordingSessionType[]).find(
              ({ startTimestamp, durationMs }) =>
                timestamp >= startTimestamp &&
                timestamp <= startTimestamp + durationMs,
            );
            if (!recording) {
              fetchNextRecording(timestamp);
              return;
            }
            onTimelineClick(recording, timestamp);
          },
        })}
        data-testid='recordingsTimeLine'
      >
        {contextMenu && (
          <ContextMenu
            x={contextMenu.x}
            y={contextMenu.y}
            items={menuItems}
            onClose={() => setContextMenu(null)}
          />
        )}
        <div
          className='absolute top-0 left-0 right-0 bottom-0'
          style={{ transform: `translateX(${offset}px)` }}
        >
          {/* Selected Region */}
          {selectedRegion && (
            <div
              id='selected-region'
              className='absolute top-0 h-full select-none'
              style={{
                left: `${
                  ((selectedRegion.start - timeline[0]) / totalMs) * 100 * scale
                }%`,
                width: `${
                  ((selectedRegion.end - selectedRegion.start) / totalMs) *
                  100 *
                  scale
                }%`,
              }}
              onMouseDown={(e) => e.stopPropagation()}
              data-testid='examineSelectedRegion'
            >
              <div
                className={`absolute left-0 top-0 z-30 h-full w-2 bg-[#FF09D8] ${
                  displayOnly ? "" : "cursor-ew-resize"
                }`}
                onMouseDown={(e) => {
                  if (displayOnly) return;
                  e.stopPropagation();
                  startXRef.current = e.clientX;
                  initialRegionRef.current = selectedRegion;
                  setDragHandle(DRAG_HANDLE.LEFT);
                }}
                data-testid='triageLeftDrag'
              />
              <div
                className={`absolute right-0 top-0 z-30 h-full w-2 bg-[#FF09D8] ${
                  displayOnly ? "" : "cursor-ew-resize"
                }`}
                onMouseDown={(e) => {
                  if (displayOnly) return;
                  e.stopPropagation();
                  startXRef.current = e.clientX;
                  initialRegionRef.current = selectedRegion;
                  setDragHandle(DRAG_HANDLE.RIGHT);
                }}
                data-testid='triageRightDrag'
              />
              {!displayOnly && (
                <div
                  className='absolute inset-y-0 z-30 left-2 right-2 bg-[#F7D3FD] cursor-grab opacity-50'
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    startXRef.current = e.clientX;
                    initialRegionRef.current = selectedRegion;
                    setDragHandle(DRAG_HANDLE.REGION);
                  }}
                  data-testid='triageDragSection'
                />
              )}
            </div>
          )}

          {zoomDragStart !== null && zoomDragEnd !== null && (
            <div
              className='absolute top-0 h-full bg-blue-300 opacity-30 border border-blue-500 z-40'
              style={{
                left: `${Math.min(zoomDragStart, zoomDragEnd) - offset}px`,
                width: `${Math.abs(zoomDragEnd - zoomDragStart)}px`,
              }}
              data-testid='zoomSelectBox'
            />
          )}

          {/* Current Position Bar */}
          {!!currentPosition && (
            <div
              className='absolute h-full border-r-2 border-red-700 border-dashed z-40'
              style={{
                left: `${
                  ((currentPosition - timeline[0]) / totalMs) * 100 * scale
                }%`,
              }}
              data-testid='cursorPosition'
            />
          )}

          {hoverTime && (
            <div
              className='absolute top-0 z-40 h-full border-x border-red-600 border-dashed'
              style={{
                left: `${((hoverTime - timeline[0]) / totalMs) * 100 * scale}%`,
              }}
            />
          )}

          {currentSection && (
            <Tooltip
              title={
                <div className='flex text-xs items-center gap-2'>
                  <AiOutlineInfoCircle size={16} /> Logs
                </div>
              }
            >
              <div
                className='absolute top-0 z-40 h-full border border-black'
                style={{
                  left: `${
                    ((currentSection[0] - timeline[0]) / totalMs) * 100 * scale
                  }%`,
                  width: `${
                    ((currentSection[1] - currentSection[0]) / totalMs) *
                    100 *
                    scale
                  }%`,
                }}
                data-testid='currentSection'
              />
            </Tooltip>
          )}

          {/* Recordings */}
          {recordings.map((recording, index) => {
            const { id, startTimestamp, durationMs } = recording;

            const startOffset =
              startTimestamp < timeline[0]
                ? 0
                : ((startTimestamp - timeline[0]) / totalMs) * 100;

            const width =
              ((startTimestamp < timeline[0]
                ? durationMs - (timeline[0] - startTimestamp)
                : durationMs) /
                totalMs) *
              100;

            return (
              <div
                key={id}
                ref={el => {
                  (recordingsRef.current[index] = el);
                }}
                className={`absolute h-full ${
                  isDemoCompany ? "bg-blue-400" : "bg-zinc-200"
                } z-20`}
                style={{
                  left: `${startOffset * scale}%`,
                  width: `${width * scale}%`,
                  minWidth: 1,
                }}
                {...(onTimelineClick && {
                  onClick: (e) => {
                    if (isMoving || e.shiftKey) return;
                    const rect =
                      recordingsRef.current[index]?.getBoundingClientRect();
                    if (rect) {
                      e.stopPropagation();
                      const multiplier =
                        (e.clientX - Math.floor(rect.left)) /
                        Math.floor(rect.width);
                      onTimelineClick(
                        recording as RecordingSessionType,
                        recording.startTimestamp + durationMs * multiplier,
                      );
                    }
                  },
                })}
                data-testid='userActivitySection'
              />
            );
          })}
          {/* Interaction Bars */}
          {interactionRanges.map(({ start, end, count }) => {
            const left = ((start - timeline[0]) / totalMs) * 100;
            const rangeWidth = ((end - start) / totalMs) * 100;
            return (
              <React.Fragment key={start}>
                <div
                  className={`absolute h-full z-[25] bg-blue-400 ${
                    onTimelineClick ? "cursor-pointer" : ""
                  }`}
                  style={{
                    left: `${left * scale}%`,
                    width: `${rangeWidth * scale}%`,
                    minWidth: activityPeriodMinWidthPx,
                  }}
                  data-testid='userActionsSection'
                />
                {showEvents && (
                  <div
                    className='absolute top-[-36px] z-30 min-w-7 text-center -translate-x-1/2'
                    style={{
                      left: `calc(${left * scale}% + ${
                        (rangeWidth * scale) / 2
                      }%)`, // center of the bar
                    }}
                    data-testid='userActionsSectionTooltip'
                  >
                    <div className='relative bg-white shadow-md rounded-md border px-2 py-1 whitespace-nowrap text-zinc-600 font-bold select-none cursor-default'>
                      {count}
                      <div className='absolute -bottom-2 left-[calc(50%_-_8px)] w-0 h-0 border-x-8 border-x-transparent border-t-8 border-t-white drop-shadow-md' />
                    </div>
                  </div>
                )}
              </React.Fragment>
            );
          })}
          {events}
        </div>
      </div>
    </div>
  );
};

export default RecordingsTimeline;
